import React, { useState, useEffect, useRef } from 'react';
import { Search, Calendar, MapPin, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { SmartImage } from './SmartImage';
import { apiService } from '../services/api';

interface SearchDropdownProps {
  query: string;
  isOpen: boolean;
  onClose: () => void;
}

interface Event {
  id: number;
  title: string;
  date?: string;
  time?: string;
  location?: string;
  imageUrl: string;
  eventUrl: string;
  category?: string;
}

/**
 * Normalize string by removing accents, punctuation, and extra spaces.
 * @param str Input string to normalize
 * @returns Normalized string
 */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Dropdown component to show search results for events
 */
export function SearchDropdown({ query, isOpen, onClose }: SearchDropdownProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Load upcoming events from API
   */
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(false);

        const upcomingEvents = await apiService.getUpcomingEvents();
        const apiEvents: Event[] = [];
        const seenEventTitles = new Set<string>();

        if (upcomingEvents.events) {
          upcomingEvents.events.forEach((event, index) => {
            const normalizedTitle = normalizeString(event.eventName);

            if (!seenEventTitles.has(normalizedTitle)) {
              seenEventTitles.add(normalizedTitle);

              const parsedDate = apiService.constructor.parseEventDate(event.date);
              const cleanLocation = apiService.constructor.cleanLocation(event.location);

              apiEvents.push({
                id: 2000 + index,
                title: event.eventName,
                date: parsedDate.date,
                time: parsedDate.time,
                location: cleanLocation,
                imageUrl: event.imageUrl || event.thumbnailUrl,
                eventUrl: event.eventUrl,
                category: 'Evento'
              });
            }
          });
        }

        setEvents(apiEvents);
      } catch (error) {
        setError(true);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && events.length === 0 && !error) {
      loadEvents();
    }
  }, [isOpen, events.length, error]);

  /**
   * Filter events based on user query
   */
  useEffect(() => {
    if (!query.trim()) {
      setFilteredEvents(events.slice(0, 6));
      return;
    }

    const searchQuery = normalizeString(query);

    const filtered = events.filter(event => {
      const normalizedEventTitle = normalizeString(event.title);
      return normalizedEventTitle.includes(searchQuery);
    });

    setFilteredEvents(filtered);
  }, [query, events]);

  /**
   * Close dropdown when user clicks outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  /**
   * Handle click on event result
   * @param event Event object
   */
  const handleEventClick = (event: Event): void => {
    if (event.eventUrl !== '#') {
      window.open(event.eventUrl, '_blank');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg backdrop-blur-md z-50 max-h-96 overflow-hidden"
    >
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Search size={16} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium truncate">
            {query ? `Resultados para "${query}" (${filteredEvents.length})` : `Eventos Disponíveis (${filteredEvents.length})`}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-6 w-6 p-0 flex-shrink-0"
        >
          <X size={14} />
        </Button>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-3 sm:p-4 space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex space-x-3 animate-pulse">
                <div className="w-16 h-12 bg-muted rounded-md flex-shrink-0"></div>
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error || filteredEvents.length === 0 ? (
          <div className="p-6 text-center">
            <Search size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">
              {error ? 'Erro ao carregar eventos' : 'Nenhum evento encontrado'}
            </p>
            <p className="text-xs text-muted-foreground">
              {error ? 'Verifique sua conexão e tente novamente' : 'Tente buscar por nome do evento'}
            </p>
          </div>
        ) : (
          <div className="p-2">
            {filteredEvents.map((event) => (
              <button
                key={event.id}
                onClick={() => handleEventClick(event)}
                className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left group"
              >
                <div
                  className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 relative image-container-subtle-pattern"
                  style={{ aspectRatio: '4/3' }}
                >
                  <SmartImage
                    src={event.imageUrl}
                    alt={event.title}
                    className="group-hover:scale-105 transition-transform duration-300"
                    containerClassName="w-full h-full rounded-md overflow-hidden"
                    sizes="64px"
                    loading="lazy"
                    blurIntensity="subtle"
                    mode="auto"
                  />
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-start justify-between">
                    <h4 className="font-medium text-sm truncate pr-2 group-hover:text-orange-primary transition-colors">
                      {event.title}
                    </h4>
                    {event.eventUrl !== '#' && (
                      <ExternalLink size={12} className="text-muted-foreground group-hover:text-orange-primary flex-shrink-0" />
                    )}
                  </div>

                  {(event.date || event.location) && (
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      {event.date && (
                        <div className="flex items-center space-x-1">
                          <Calendar size={10} className="flex-shrink-0" />
                          <span className="truncate">{event.date}</span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center space-x-1">
                          <MapPin size={10} className="flex-shrink-0" />
                          <span className="truncate">{event.location.split(',')[0]}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {event.category && (
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary" className="text-xs px-2 py-0.5">
                        {event.category}
                      </Badge>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
